import merge from 'lodash/merge';
import uniq from 'lodash/uniq';
import { escapeRegexString } from '@xmatters/common/utils/regexUtil';

export const stringCompare = (a, b) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }

  return 0;
};

/**
 * 1. In ascending order, so 0, 1, 2, 3, ..., 9; a, b, c, d, ..., z.
 * 2. Numbers before letters, so 0ab comes before ab0.
 # 3. Case insensitive, so a comes before B.
 * @param a {number|string}
 * @param b {number|string}
 * @returns {number}
 */
export function alphaNumericalCompare(a, b) {
  // JavaScript's toLowerCase is quite tolerant, so "ABc --- ### 3".toLowerCase() won't crash; it'll only convert letters.
  return stringCompare(a.toString().toLowerCase(), b.toString().toLowerCase());
}

export function textContainsAtIndex(text, phrase, index) {
  if (typeof text === 'string' || text instanceof String) {
    return text.substr(index, phrase.length) === phrase;
  }
  return undefined;
}

/**
 * Creates a string parser that will split given `text` into text snippets and variables:
 *  variables are parts of the `text` surrounded by `start` and `end` strings,
 *  while the text snippets are the rest of the text. Text snippets will always have
 *  one more element than variables.
 *
 * @param {string} start - start of a variable
 * @param {string} end - end of the variable
 * @param {string} escapeCharacter - character that signifies that next character should be treated as a literal character
 * @param {boolean} isEndOptional - if end is optional, the end of text will count as the end of 2nd group
 * @returns {Function} function to parse the array into text snippets and variables
 */
export function createTemplateParser(
  start,
  end,
  escapeCharacter,
  isEndOptional = false,
) {
  const TEXT_SNIPPET = 0;
  const VARIABLE = 1;
  return text => {
    const textSnippets = [];
    const variables = [];
    let isNextCharacterEscaped = false;
    let currentGroupNum = TEXT_SNIPPET;
    let currentString = '';
    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const currentGroup =
        currentGroupNum === TEXT_SNIPPET ? textSnippets : variables;
      if (isNextCharacterEscaped) {
        currentString += char;
        isNextCharacterEscaped = false;
      } else if (char === escapeCharacter) {
        isNextCharacterEscaped = true;
      } else if (
        currentGroupNum === TEXT_SNIPPET &&
        textContainsAtIndex(text, start, i)
      ) {
        currentGroup.push(currentString);
        currentString = '';
        currentGroupNum = VARIABLE;
        i += start.length - 1;
      } else if (
        currentGroupNum === VARIABLE &&
        textContainsAtIndex(text, end, i)
      ) {
        currentGroup.push(currentString);
        currentString = '';
        currentGroupNum = TEXT_SNIPPET;
        i += end.length - 1;
      } else {
        currentString += char;
      }
    }
    if (isNextCharacterEscaped) {
      currentString += escapeCharacter;
    }
    if (currentGroupNum === VARIABLE) {
      if (isEndOptional) {
        variables.push(currentString);
        textSnippets.push('');
      } else {
        textSnippets[textSnippets.length - 1] =
          textSnippets[textSnippets.length - 1] + start + currentString;
      }
    } else {
      textSnippets.push(currentString);
    }
    return {
      textSnippets,
      variables,
    };
  };
}

export function trimAndRemoveRepeatSpaces(value) {
  return value.replace(/^\s+/, '').replace(/\s+$/, '').replace(/\s+/g, ' ');
}

const endsWithEscape = (str, escape = '\\') => {
  const len = str.length - 1;
  let i = len;
  for (; i >= 0 && str[i] === escape; i -= 1);
  // Basically, if there are an even number the escapse are escaped:
  return (len - i) % 2 !== 0;
};

/*
 * Splits string on all instances of a character (splitter) but allows escaping that character.
 *   - Basically the trick here is we split first, and then iterate through and whichever items
 *   end with the escape joins to the next
 *
 * Examples:
 * splitEscaped('hello+World') // => ['hello+World']
 * splitEscaped('hello+World', '+') // => ['hello', 'World']
 * splitEscaped('hello\\+World', '+') // => ['hello\\+World']
 * splitEscaped('hello\\+World+NOT', '+') // => ['hello\\+World', 'NOT']
 */
export const splitEscaped = (str, splitter, escape = '\\') => {
  let prevWasEscaped = false;
  return str.split(splitter).reduce((acc, curStr) => {
    let val = curStr;
    if (prevWasEscaped) {
      val = acc.pop() + splitter + curStr;
    }
    prevWasEscaped = endsWithEscape(curStr, escape);
    return [...acc, val];
  }, []);
};

const ALL_ALPHA_NUM =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const ALL_LOWER_ALPHA_NUM = 'abcdefghijklmnopqrstuvwxyz0123456789';
export function generateRandomAlphanumericString(size, params = {}) {
  const includeCaps = params.includeCaps || false;
  const chars = includeCaps ? ALL_ALPHA_NUM : ALL_LOWER_ALPHA_NUM;
  const getRandomChar = () =>
    chars.charAt(Math.floor(Math.random() * chars.length));

  let code = '';
  for (let i = 0; i < size; i += 1) {
    code += getRandomChar();
  }
  return code;
}

const getTextSearchMatches = (text, searchString) => {
  if (!searchString || searchString.trim().length === 0) {
    return [];
  }

  if (!text || typeof text !== 'string') {
    return [];
  }

  const searchTerms = uniq(
    escapeRegexString(trimAndRemoveRepeatSpaces(searchString))
      .split(' ')
      .sort((a, b) => b.length - a.length),
    true,
  );

  const regex = new RegExp(
    searchTerms
      .map(term => `(${term})`) // each term gets it's own capture group
      .join('|'),
    'gi',
  );

  return [...text.matchAll(regex)];
};

const getSortedMatchRanges = matches =>
  matches
    .sort((a, b) => a.index - b.index)
    .map(match => [match.index, match.index + match[0].length]);

/**
 * returns the indices of text that matches *any* of the terms in the space-delimited searchString
 */
export const textSearchMatchIndexes = (text, searchString) => {
  const matches = getTextSearchMatches(text, searchString);
  return getSortedMatchRanges(matches);
};

/**
 * return the indices of text that matches *all* of the terms in the space-delimited searchString.
 * If only some of the terms are matched, [] is returned.
 */
export const textSearchAllMatchIndexes = (text, searchString) => {
  const matches = getTextSearchMatches(text, searchString);
  const someTermsNotMatched = merge([], ...matches).some(
    term => term === undefined,
  );
  if (someTermsNotMatched) {
    return [];
  }

  return getSortedMatchRanges(matches);
};

export const countSubstringMatches = (str, subStr) => {
  const regexStr = escapeRegexString(subStr);
  return str?.match(new RegExp(regexStr, 'g'))?.length || 0;
};

// Extracts parameter from url
// Used for parsing link params embedded in payloads used for paging
// Eg. { self: '/webapi/xm/1/people/200005/group-memberships?offset=0&limit=100' }
export function getParameterByName(name, url) {
  // Disabled for regexp parsing
  // eslint-disable-next-line no-useless-escape
  const nameReplaced = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${nameReplaced}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export const isBase64 = (str, opts = {}) => {
  const { isEncoded } = opts;
  try {
    // If already encoded attempt to decode and then re-encode; otherwise do the opposite:
    const encodingToggledStr = isEncoded ? btoa(atob(str)) : atob(btoa(str));
    return encodingToggledStr === str; // No changes detected
  } catch (err) {
    return false;
  }
};

export const encodeReduxFieldName = str => {
  // encode to RFC 3986 standard (which additionally reserves !, ', (, ), and *)
  // and in addition, also encode . character
  return encodeURIComponent(str).replace(
    /[~!'()*.]/g,
    c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
};

export const decodeReduxFieldName = str => {
  // decode RFC 3986 standard and . character
  return str.replace(/%[a-fA-F0-9]{2}/gi, match =>
    String.fromCharCode(parseInt(match.replace('%', ''), 16)),
  );
};

export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
